import React, { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { SocketContext } from "../context/SocketProvider";
import { DEFAULT_ROOM, HOME_PATH } from "../constants/gameStages";
import { useRoleBackground } from "../hooks/useRoleBackground";
import { FaChevronRight } from "react-icons/fa6";

/**
 * DataCollectionSettings — shown after pressing Start with Data Collection ON.
 * Displays the system-generated Player ID so the user can write it on their consent form.
 * Back → home lobby, Next → enter the game (gamemode selection).
 */
const DataCollectionSettings = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { role, userId, socket, setConsentSeen } = useContext(SocketContext);
  const isDescriber = role === "describer";
  const { bgStyle, className } = useRoleBackground(isDescriber ? "pink" : "blue");

  const handleNext = () => {
    // Set default gamemode to "normal" and skip game mode selection pages, but show the how to play page
    const defaultMode = "normal";
    socket.emit("game_started", { room: DEFAULT_ROOM });
    socket.emit("gamemode_selected", { mode: defaultMode, room: DEFAULT_ROOM });
    socket.emit("gamemode_confirmed", { mode: defaultMode, room: DEFAULT_ROOM });
    // Skip /gamemode and /waitingforgamemode, go to showgamemode (how to play)
    navigate("/showgamemode", { state: { mode: defaultMode } });
  };

  const bgDark = isDescriber ? "bg-pink400" : "bg-blue400";
  const textAccent = isDescriber ? "text-pink400" : "text-blue400";
  const borderAccent = isDescriber ? "border-pink400" : "border-blue400";

  // If the ID hasn't arrived yet (race with socket), request it from the server
  useEffect(() => {
    if (!userId) {
      socket.emit("request_player_id", {});
    }
  }, [userId, socket]);

  // Show the badge as soon as this page mounts (user has seen the ID page)
  useEffect(() => {
    setConsentSeen(true);
  }, [setConsentSeen]);

  return (
    <div className={className} style={bgStyle}>
      <div className="min-h-screen flex flex-col items-center pt-[128px] px-4">
        {/* Card */}
        <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className={`${bgDark} py-4 px-6`}>
            <h2 className="text-white text-[22px] font-bold text-center font-['Gabarito']">
              {t("dataCollection.title", "DATA COLLECTION SETTINGS")}
            </h2>
          </div>

          {/* Body */}
          <div className="px-10 pt-8 pb-2 text-center">
            <p className="text-lg mb-4">
              {t("dataCollection.selected", "You and your partner have selected for")}{" "}
              <strong>{t("dataCollection.dataCollection", "Data Collection")}</strong>{" "}
              {t("dataCollection.toBeOn", "to be ON.")}
            </p>
            <p className="text-lg mb-2">
              {t("dataCollection.consent", "All game results will be stored and used for research purposes. Please write your unique Player ID on your consent form.")}
            </p>

            {/* Player ID */}
            <div className="my-10">
              {userId ? (
                <p className="text-[42px] font-['Gabarito'] font-bold tracking-wide">
                  {t("dataCollection.playerId", "PLAYER ID:")} {userId}
                </p>
              ) : (
                <p className="text-2xl text-gray-400 font-['Gabarito']">
                  {t("dataCollection.generating", "Generating...")}
                </p>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-center gap-4 pb-10">
            <button
              onClick={() => navigate(HOME_PATH)}
              className={`border-2 ${borderAccent} ${textAccent} font-bold font-['Gabarito'] text-xl px-8 py-3 rounded-xl flex items-center gap-2 transition-all hover:shadow-[inset_0_3px_0_0_rgba(0,0,0,0.2)]`}
            >
              {t("button.back", "Back")}
              <FaChevronRight />
            </button>
            <button
              onClick={handleNext}
              className={`${bgDark} text-white font-bold font-['Gabarito'] text-xl px-8 py-3 rounded-xl flex items-center gap-2 transition-all hover:shadow-[inset_0_3px_0_0_rgba(0,0,0,0.2)]`}
            >
              {t("button.next", "Next")}
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataCollectionSettings;
